import { useState, useCallback } from 'react';
import type { ModalType } from '@/components/ui/Modal';



interface ModalState {
  isOpen: boolean;
  type: ModalType;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  showCancel: boolean;
  onConfirm?: () => void | Promise<void>;
  loading: boolean;
}

interface ShowModalOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void | Promise<void>;
}

interface UseModalReturn {
  modalState: ModalState;
  showSuccess: (options: ShowModalOptions | string) => void;
  showError: (options: ShowModalOptions | string) => void;
  showWarning: (options: ShowModalOptions | string) => void;
  showInfo: (options: ShowModalOptions | string) => void;
  showConfirm: (options: ShowModalOptions) => void;
  showDelete: (options: ShowModalOptions) => void;
  closeModal: () => void;
  setLoading: (loading: boolean) => void;
}



const initialState: ModalState = {
  isOpen: false,
  type: 'info',
  title: '',
  message: '',
  confirmText: 'Aceptar',
  cancelText: 'Cancelar',
  showCancel: false,
  onConfirm: undefined,
  loading: false
};



export function useModal(): UseModalReturn {
  const [modalState, setModalState] = useState<ModalState>(initialState);

  const closeModal = useCallback(() => {
    setModalState(prev => ({ ...prev, isOpen: false, loading: false }));
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setModalState(prev => ({ ...prev, loading }));
  }, []);

  const showModal = useCallback((
    type: ModalType,
    options: ShowModalOptions | string,
    showCancel: boolean = false
  ) => {
    const opts = typeof options === 'string' 
      ? { message: options } 
      : options;

    setModalState({
      isOpen: true,
      type,
      title: opts.title || '',
      message: opts.message,
      confirmText: opts.confirmText || 'Aceptar',
      cancelText: opts.cancelText || 'Cancelar',
      showCancel,
      onConfirm: opts.onConfirm,
      loading: false
    });
  }, []);

  const showSuccess = useCallback((options: ShowModalOptions | string) => {
    showModal('success', options);
  }, [showModal]);

  const showError = useCallback((options: ShowModalOptions | string) => {
    showModal('error', options);
  }, [showModal]);

  const showWarning = useCallback((options: ShowModalOptions | string) => {
    showModal('warning', options);
  }, [showModal]);

  const showInfo = useCallback((options: ShowModalOptions | string) => {
    showModal('info', options);
  }, [showModal]);

  const showConfirm = useCallback((options: ShowModalOptions) => {
    showModal('confirm', options, true);
  }, [showModal]);

  const showDelete = useCallback((options: ShowModalOptions) => {
    showModal('delete', {
      ...options,
      confirmText: options.confirmText || 'Eliminar',
    }, true);
  }, [showModal]);

  return {
    modalState,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirm,
    showDelete,
    closeModal,
    setLoading
  };
}

export default useModal;